<template>
  <div class="dv2-section">
    <SectionHeader icon="storefront" title="Análise de Fornecedores" anchor="sec-participantes" />

    <div class="dv2-grid dv2-grid--kpi-chart">
      <KpiCard :columns="2" :loading="loading">
        <KpiTile v-for="tile in suppliersKpis" :key="tile.label" v-bind="tile" />
      </KpiCard>

      <HighchartsCard
        title="Fornecedores engajados por mês — com e sem pedido"
        :tooltip="tooltips.suppliers"
        :loading="loading"
        :options="suppliersOptions"
      />
    </div>

    <div class="dv2-grid dv2-grid--full">
      <TableCard
        title="Curva ABC de fornecedores"
        :tooltip="tooltips.suppliersCurve"
        :loading="loading"
      >
        <a-table
          :columns="columns"
          :data-source="suppliersCurve"
          :pagination="{ pageSize: 10 }"
          :row-key="(row) => row.name"
          size="small"
        />
      </TableCard>
    </div>
  </div>
</template>

<script>
import SectionHeader from './SectionHeader.vue';
import KpiCard from './KpiCard.vue';
import KpiTile from './KpiTile.vue';
import HighchartsCard from './HighchartsCard.vue';
import TableCard from './TableCard.vue';
import DashboardV2Service from '@/services/v3/dashboard/DashboardV2Service';
import { baseTheme, xAxisDefaults, yAxisDefaults, pluck } from '../shared/highchartsTheme';
import { BRAND } from '../shared/constants';
import {
  brl, pctBR, buildDeltaColumns, formatMonthLabel,
} from '../shared/format';
import { buildKpiTiles } from '../shared/kpi';

const service = new DashboardV2Service();

const TOOLTIPS = {
  suppliers: 'Fornecedores engajados por mês, separados entre os que receberam pedido e os que participaram sem ganhar.',
  suppliersCurve: 'Fornecedores ordenados pelo valor comprado, com participação no total comprado e taxa de vitória.',
};

const signed = (v, decimals = 1) => `${v >= 0 ? '+' : '-'}${pctBR(Math.abs(v), decimals)}`;

const SUPPLIERS_STYLE = {
  inRegistry: {
    label: 'No cadastro',
    tooltip: 'Fornecedores ativos no cadastro. É a base para as taxas de engajamento.',
    format: 'int', valueColor: BRAND.preto, captionColor: BRAND.muted,
    caption: 'base de fornecedores ativos',
    // tamanho do cadastro é uma métrica base, não julgada como boa/ruim
    deltaNeutral: true,
  },
  acquired: {
    label: 'Captados',
    tooltip: 'Novos fornecedores cadastrados no período — captação da equipe de compras.',
    format: 'int', valueColor: BRAND.laranja, captionColor: BRAND.laranja,
    captionTemplate: (raw) => `${pctBR(raw.pctOfRegistry)} do cadastro · ${raw.alreadyEngaged} já engajados`,
  },
  engaged: {
    label: 'Engajados',
    tooltip: 'Fornecedores que enviaram ao menos uma proposta no período. A barra mostra a participação do cadastro.',
    format: 'int', valueColor: BRAND.azul, captionColor: BRAND.azul, progressColor: BRAND.azul,
    captionTemplate: (raw) => `${pctBR(raw.progress)} do cadastro · mês ${signed(raw.monthDeltaPct)}`,
  },
  withOrder: {
    label: 'Com pedido',
    tooltip: 'Fornecedores que ganharam ao menos uma cotação. A barra mostra a conversão engajado → com pedido.',
    format: 'int', valueColor: BRAND.verde, captionColor: BRAND.verde, progressColor: BRAND.verde,
    captionTemplate: (raw) => `${pctBR(raw.progress)} dos engajados · ${pctBR(raw.pctOfRegistry)} do cadastro`,
  },
};

export default {
  name: 'SuppliersSection',

  components: {
    SectionHeader, KpiCard, KpiTile, HighchartsCard, TableCard,
  },

  props: {
    filters: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      loading: true,
      tooltips: TOOLTIPS,
      suppliersKpis: [],
      suppliersChart: null,
      suppliersCurve: [],
    };
  },

  computed: {
    suppliersOptions() {
      if (!this.suppliersChart) return {};
      const rows = this.suppliersChart;
      const withOrder = pluck(rows, 'withOrder');
      const withoutOrder = pluck(rows, 'withoutOrder');
      const avgPerQuote = pluck(rows, 'avgPerQuote');
      const theme = baseTheme();

      return {
        ...theme,
        chart: { ...theme.chart, type: 'column' },
        xAxis: xAxisDefaults({ categories: pluck(rows, 'month').map(formatMonthLabel) }),
        yAxis: [
          yAxisDefaults({ title: { text: 'Fornecedores' } }),
          yAxisDefaults({
            opposite: true, gridLineWidth: 0, max: 5, title: { text: 'Média por cotação' }, labels: { format: '{value}' },
          }),
        ],
        tooltip: { ...theme.tooltip, shared: true },
        plotOptions: { ...theme.plotOptions, column: { stacking: 'normal', borderRadius: 2, groupPadding: 0.08 } },
        series: [
          { type: 'column', name: 'Engajados com pedido', data: withOrder, color: BRAND.verde },
          { type: 'column', name: 'Engajados sem pedido', data: withoutOrder, color: '#9C948C' },
          {
            type: 'spline', name: 'Média por cotação', data: avgPerQuote, yAxis: 1, color: BRAND.laranja, lineWidth: 3,
            marker: { fillColor: '#fff', lineColor: BRAND.laranja, lineWidth: 2, radius: 4 },
          },
        ],
      };
    },

    columns() {
      const h = this.$createElement;
      const totalApproved = this.suppliersCurve.reduce((sum, row) => sum + row.approved, 0) || 1;

      const base = [
        {
          title: 'Fornecedor',
          dataIndex: 'name',
          customRender: (text) => h('span', { style: { fontWeight: 700 } }, text),
        },
        { title: 'Cotações vencidas', dataIndex: 'wins', align: 'right' },
        {
          title: 'Valor comprado',
          dataIndex: 'approved',
          align: 'right',
          customRender: (v) => brl(v),
        },
        {
          title: 'Participação (%)',
          dataIndex: 'approved',
          align: 'right',
          width: 190,
          customRender: (v) => {
            const percent = (v / totalApproved) * 100;
            return h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' } }, [
              h('a-progress', {
                style: { width: '90px' },
                props: { percent, showInfo: false, strokeColor: BRAND.azul, strokeWidth: 6 },
              }),
              h('span', { style: { fontSize: '11px', fontWeight: 600 } }, pctBR(percent)),
            ]);
          },
        },
        {
          title: 'Taxa de vitória',
          dataIndex: 'winRate',
          align: 'right',
          customRender: (v) => pctBR(v),
        },
      ];

      return base.concat(buildDeltaColumns(h));
    },
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.fetch();
      },
    },
  },

  created() {
    this.fetch();
  },

  methods: {
    fetch() {
      this.loading = true;
      service.getFornecedores(this.filters).then(({ data }) => {
        this.suppliersKpis = buildKpiTiles(data.kpis, SUPPLIERS_STYLE, { valueFontSize: '36px' });
        this.suppliersChart = data.historic;
        this.suppliersCurve = data.ranking;
        this.loading = false;
      });
    },
  },
};
</script>
